import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import Checkbox from 'components/shared/form/Checkbox';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import type Upload from 'hooks/useDirectUpload/Upload';
import { BULK_IMPORT_TYPE_LABELS, BulkImportType, createBulkImport } from 'services/api/admin/bulkImports';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import DownloadFilesIcon from 'svg/DownloadFilesIcon';

type ImportTypeOption = { id: BulkImportType; name: string };
const IMPORT_TYPES: ImportTypeOption[] = [
  { id: BulkImportType.USERS, name: BULK_IMPORT_TYPE_LABELS[BulkImportType.USERS] },
  { id: BulkImportType.LOCATIONS, name: BULK_IMPORT_TYPE_LABELS[BulkImportType.LOCATIONS] },
];

// Windows browsers commonly report .csv files as application/vnd.ms-excel.
const CSV_TYPES = {
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.csv'],
};

type ColumnSpec = { name: string; required: boolean; hint: string };

const CSV_COLUMNS: Record<BulkImportType, ColumnSpec[]> = {
  [BulkImportType.USERS]: [
    { name: 'client', required: true, hint: 'Client name — each row can target a different client' },
    { name: 'email', required: true, hint: 'Also used to match existing users' },
    { name: 'name', required: true, hint: 'Full name' },
    { name: 'role', required: true, hint: 'Role name, e.g. Navigator' },
    { name: 'credential', required: false, hint: 'Abbreviation, e.g. RN' },
    {
      name: 'location_names',
      required: false,
      hint: 'Locations in the row’s client — the user gets each location’s groups; separate multiple with | or commas',
    },
    { name: 'external_id', required: false, hint: 'Stored on the user' },
  ],
  [BulkImportType.LOCATIONS]: [
    { name: 'client', required: true, hint: 'Client name — each row can target a different client' },
    { name: 'location_name', required: true, hint: 'Also used to match existing locations in the client' },
    {
      name: 'timezone',
      required: true,
      hint: 'Same options as the location form, e.g. Eastern Time or America/New_York',
    },
    {
      name: 'group_types',
      required: false,
      hint: 'Group type display names, e.g. SNF — one group per type is created, named after the location; separate multiple with | or commas',
    },
    { name: 'street_1', required: false, hint: 'Street address' },
    { name: 'street_2', required: false, hint: 'Suite, unit, floor' },
    { name: 'city', required: false, hint: 'City' },
    { name: 'state', required: false, hint: 'State abbreviation, e.g. IN' },
    { name: 'zip', required: false, hint: 'ZIP code' },
  ],
};

const TEMPLATE_FILENAMES: Record<BulkImportType, string> = {
  [BulkImportType.USERS]: 'bulk_user_import_template.csv',
  [BulkImportType.LOCATIONS]: 'bulk_location_import_template.csv',
};

const templateHref = (type: BulkImportType) =>
  `data:text/csv;charset=utf-8,${encodeURIComponent(`${CSV_COLUMNS[type].map((c) => c.name).join(',')}\n`)}`;

// FileReader instead of Blob#text so the header sniff also works under jsdom.
function readFileHead(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file.slice(0, 4096));
  });
}

// Same normalization the API applies to headers (strip, downcase, BOM).
async function parseCsvHeaders(file: File): Promise<string[]> {
  const text = await readFileHead(file);
  return (text.split(/\r?\n/)[0] ?? '')
    .replace(/^\uFEFF/, '')
    .toLowerCase()
    .split(',')
    .map((header) => header.trim().replace(/^"|"$/g, ''))
    .filter(Boolean);
}

// The required header sets identify the import type unambiguously (users
// needs email/name/role, locations needs location_name/timezone), so a
// recognizable file selects its own type. Ambiguous or unknown headers
// resolve to null and leave the selection to the operator.
function detectTypeFromHeaders(headers: string[]): ImportTypeOption | null {
  const matches = IMPORT_TYPES.filter((type) =>
    CSV_COLUMNS[type.id].every((column) => !column.required || headers.includes(column.name))
  );
  return matches.length === 1 ? matches[0] : null;
}

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

export default function BulkImportNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToastActions();

  // No default type — a deep link (?type=) or the uploaded file's headers
  // select one; otherwise the operator picks.
  const [importType, setImportType] = useState<ImportTypeOption | null>(
    () => IMPORT_TYPES.find((t) => t.id === searchParams.get('type')) ?? null
  );
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);
  const [fileHeaders, setFileHeaders] = useState<string[] | null>(null);
  const detectedFile = useRef<File | null>(null);

  const completedUpload = uploads.find((u) => u.isComplete && u.signedId);

  const handleUploads = (next: Upload[]) => {
    setUploads(next);

    const file = next[0]?.file;
    if (!file) {
      detectedFile.current = null;
      setFileHeaders(null);
      return;
    }
    if (file === detectedFile.current) return;

    detectedFile.current = file;
    parseCsvHeaders(file)
      .then((headers) => {
        setFileHeaders(headers);
        const detected = detectTypeFromHeaders(headers);
        if (detected) setImportType(detected);
      })
      .catch(() => setFileHeaders(null));
  };

  const ignoredColumns =
    fileHeaders && importType
      ? fileHeaders.filter((header) => !CSV_COLUMNS[importType.id].some((column) => column.name === header))
      : [];

  // The preview would only reject the file server-side; when the headers are
  // readable and a required column is absent, block the submit up front.
  // Unreadable headers (fileHeaders null) don't block — the server decides.
  const missingRequired =
    fileHeaders && importType
      ? CSV_COLUMNS[importType.id].some((column) => column.required && !fileHeaders.includes(column.name))
      : false;

  const canSubmit = !!completedUpload && !uploading && !!importType && !missingRequired;

  const { mutate, isPending } = useMutation({
    mutationFn: createBulkImport,
    onSuccess: (res) => navigate(`/admin/bulk-import/${res.id}`),
    onError: () => addToast({ text: 'There was an error starting the import. Please try again.' }),
  });

  const submit = () => {
    if (!canSubmit || !completedUpload?.signedId || !importType) return;

    mutate({
      importType: importType.id,
      signedId: completedUpload.signedId,
      sendInvite: importType.id === BulkImportType.USERS ? sendInvite : false,
    });
  };

  return (
    <AdminFormPage>
      <FormHeader title='New Bulk Import' />
      <Form>
        <FormSection>
          <SectionHeader>Import Details</SectionHeader>
          <InputGroup title='Resource Type'>
            <Select<ImportTypeOption>
              options={IMPORT_TYPES}
              value={importType}
              onChange={(option) => option && setImportType(option)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select a resource type'
            />
          </InputGroup>
          <InputGroup title='CSV File'>
            <FileInputComponent
              onChange={handleUploads}
              onUploading={setUploading}
              renderUploadsInternally
              allowedTypes={CSV_TYPES}
            />
            {importType ? (
              <FormatCard>
                <FormatHeader>
                  <FormatTitle>CSV columns · {BULK_IMPORT_TYPE_LABELS[importType.id]}</FormatTitle>
                  <TemplateLink href={templateHref(importType.id)} download={TEMPLATE_FILENAMES[importType.id]}>
                    <DownloadFilesIcon width={12} height={12} />
                    Download template
                  </TemplateLink>
                </FormatHeader>
                {CSV_COLUMNS[importType.id].map((column) => (
                  <FormatRow key={column.name} $withStatus={!!fileHeaders}>
                    <ColumnName>{column.name}</ColumnName>
                    <RequirementPill $required={column.required}>
                      {column.required ? 'Required' : 'Optional'}
                    </RequirementPill>
                    {fileHeaders && <FileStatusPill column={column} headers={fileHeaders} />}
                    <ColumnHint>{column.hint}</ColumnHint>
                  </FormatRow>
                ))}
                {ignoredColumns.length > 0 && (
                  <IgnoredNote>
                    Ignored columns in file: <strong>{ignoredColumns.join(', ')}</strong> — not part of the{' '}
                    {BULK_IMPORT_TYPE_LABELS[importType.id]} format, so they will be skipped.
                  </IgnoredNote>
                )}
              </FormatCard>
            ) : (
              <FormatNote>
                Choose a resource type — or upload a file and the type is detected from its columns.
              </FormatNote>
            )}
          </InputGroup>
          {importType?.id === BulkImportType.USERS && (
            <InputGroup>
              <Checkbox
                checked={sendInvite}
                onChange={setSendInvite}
                label='Send invites now'
                dataCyLabel='sendInvite'
              />
            </InputGroup>
          )}
        </FormSection>

        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ flex: 1 }} />
          <ButtonGroup>
            <Button variant='ghost' onClick={() => navigate('/admin/bulk-import')} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canSubmit || isPending} loading={isPending}>
              Upload &amp; Preview
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </AdminFormPage>
  );
}

function FileStatusPill({ column, headers }: { column: ColumnSpec; headers: string[] }) {
  if (headers.includes(column.name)) {
    return (
      <StatusPill $background={colors.green10} $color={colors.green100}>
        In file
      </StatusPill>
    );
  }
  if (column.required) {
    return (
      <StatusPill $background={colors.red10} $color={colors.red100}>
        Missing
      </StatusPill>
    );
  }
  return (
    <StatusPill $background={colors.black5} $color={colors.black50}>
      Not in file
    </StatusPill>
  );
}

const StatusPill = styled.span<{ $background: string; $color: string }>`
  justify-self: start;
  padding: 1px 8px;
  border-radius: 24px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
`;

const IgnoredNote = styled.div`
  padding: 8px 14px;
  border-top: 1px solid ${colors.black5};
  background: ${colors.black3};
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black50};

  strong {
    color: ${colors.black75};
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    font-weight: var(--font-weight-bold);
  }
`;

const FormatNote = styled.div`
  margin-top: 12px;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black50};
`;

const FormatCard = styled.div`
  margin-top: 12px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
`;

const FormatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: ${colors.black3};
  border-bottom: 1px solid ${colors.black10};
`;

const FormatTitle = styled.span`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.black50};
`;

const TemplateLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.blue100};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FormatRow = styled.div<{ $withStatus?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $withStatus }) => ($withStatus ? '110px 72px 76px 1fr' : '110px 72px 1fr')};
  align-items: baseline;
  gap: 12px;
  padding: 8px 14px;

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const ColumnName = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: ${colors.black100};
`;

const RequirementPill = styled.span<{ $required: boolean }>`
  justify-self: start;
  padding: 1px 8px;
  border-radius: 24px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  background: ${({ $required }) => ($required ? colors.blue10 : colors.black5)};
  color: ${({ $required }) => ($required ? colors.blue100 : colors.black50)};
`;

const ColumnHint = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black50};
`;
